<!-- Order Details Page -->
<app-admin-header [header]="'Order Details' | translator" [links]="[]" [breadcrumbs]="breadcrumbs" [stats]="[]">
</app-admin-header>

<div class="container-fluid p-4">
  <!-- Loading State -->
  <div *ngIf="loading" class="loading-container">
    <div class="loading-spinner">
      <i class="pi pi-spinner pi-spin"></i>
      <p>{{'Loading order details...' | translator}}</p>
    </div>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !loading" class="error-container">
    <div class="error-message">
      <i class="pi pi-exclamation-triangle"></i>
      <h3>{{ "Error Loading Order Details" | translator }}</h3>
      <p>{{ error }}</p>
      <button class="btn btn-primary" (click)="loadOrderDetails()">
        <i class="pi pi-refresh error-refresh"></i>
        {{ "Try Again" | translator }}
      </button>
    </div>
  </div>

  <!-- Order Details Content -->
  <div *ngIf="orderDetails && !loading && !error" class="order-content">
    <div class="order-bg">
    <!-- Order Header -->
    <div class="order-header-section mb-4">
      <div class="row">
        <div class="col-md-8">
          <div class="d-flex align-items-center flex-wrap gap-2">
            <button *ngIf="permissionService.hasPermission('ORDER', 'r')" class="v4-btn-every" (click)="goBack()">
              <i class="pi pi-arrow-right"></i>
              {{ "Back" | translator }}
            </button>
            <div>
              <h2 class="order-title mb-1">{{ orderDetails.orderId }}</h2>
              <p class="order-meta text-muted mb-0">
                {{ "Created" | translator }}: {{ formatDate(orderDetails.createdAt) }}
                <span *ngIf="orderDetails.updatedAt && orderDetails.updatedAt !== orderDetails.createdAt">
                  | {{ "Updated" | translator }}: {{ formatDate(orderDetails.updatedAt) }}
                </span>
              </p>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="d-flex justify-content-end align-items-center gap-2">
            <p-menu class="p-menu-custom" styleClass="p-menu-custom" #statusMenu [model]="statusMenuItems" [popup]="true" appendTo="body"></p-menu>
            <div class="d-flex align-items-center gap-2">
              <p-tag 
                [value]="translate(orderDetails.orderStatus)" 
                [severity]="getStatusSeverity(orderDetails.orderStatus)"
                styleClass="p-tag-lg">
              </p-tag>
              <button 
                *ngIf="statusMenuItems.length > 0"
                type="button" 
                pButton 
                icon="pi pi-chevron-down"
                class="p-button-text p-button-plain"
                (click)="statusMenu.toggle($event)"
                [disabled]="isUpdatingStatus"
                [title]="'Actions' | translator">
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Main Content -->
      <div class="col-lg-8">
       

        <!-- Product Information -->
        <div class="detail-card mb-4 11">
          <div class="card-header">
            <h4 class="card-title">
              <i class="pi pi-shopping-cart me-2"></i>
              {{ "Product Information" | translator }}
            </h4>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-8">
                <div class="info-item">
                  <label class="info-label">{{ "Product Name" | translator }}</label>
                  <p class="info-value">{{ orderDetails.productService.productName }}</p>
                </div>
                <div class="info-item" *ngIf="orderDetails.productService.productType">
                  <label class="info-label">{{ "Product Type" | translator }}</label>
                  <p class="info-value">{{ orderDetails.productService.productType }}</p>
                </div>
              </div>
              <div class="col-md-4">
                <div class="pricing-summary">
                  <div class="price-item">
                    <span class="price-label">{{ "Unit Price" | translator }}:</span>
                    <span class="price-value">
                      {{ formatCurrency(orderDetails.productService.unitPrice, orderDetails.productService.currency) }}
                      <img *ngIf="isValidNumber(orderDetails.productService.unitPrice)" src="assets/pdf/graphics/Saudi_Riyal_Symbol-1.png" alt="SAR" class="currency-icon-inline currency-12" />
                    </span>
                  </div>
                  <div class="price-item" *ngIf="orderDetails.productService.vatAmount">
                    <span class="price-label">{{ "VAT" | translator }}:</span>
                    <span class="price-value">
                      {{ formatCurrency(orderDetails.productService.vatAmount, orderDetails.productService.currency) }}
                      <img src="assets/pdf/graphics/Saudi_Riyal_Symbol-1.png" alt="SAR" class="currency-icon-inline currency-12" />
                    </span>
                  </div>
                  <hr class="price-divider">
                  <div class="price-item total-price">
                    <span class="price-label">{{ "Total Amount" | translator }}:</span>
                    <span class="price-value">
                      {{ formatCurrency(orderDetails.productService.totalAmount, orderDetails.productService.currency) }}
                      <img *ngIf="isValidNumber(orderDetails.productService.totalAmount)" src="assets/pdf/graphics/Saudi_Riyal_Symbol-1.png" alt="SAR" class="currency-icon-inline currency-14" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
         <!-- Survey Information -->
         <div class="detail-card mb-4 66" *ngIf="orderDetails.survey">
          <div class="card-header">
            <h4 class="card-title">
              <i class="pi pi-clipboard me-2"></i>
              {{ "Survey Information" | translator }}
            </h4>
          </div>
          <div class="card-body">
            <div class="survey-info">
              <div class="survey-actions mt-3">
                <button 
                  class="v4-btn-every"
                  (click)="openSurveyModal()"
                  title="View Survey Details">
                  <i class="pi pi-eye me-2"></i>
                  {{ "View Survey Details" | translator }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Notes -->
        <div class="detail-card mb-4 22" *ngIf="orderDetails.orderNotes">
          <div class="card-header">
            <h4 class="card-title">
              <i class="pi pi-file-text me-2"></i>
              {{ "Order Notes" | translator }}
            </h4>
          </div>
          <div class="card-body">
            <p class="order-notes">{{ orderDetails.orderNotes }}</p>
          </div>
        </div>

        <!-- Order History -->
        <div class="detail-card mb-4 33" *ngIf="orderDetails.orderHistory && orderDetails.orderHistory.length > 0">
          <div class="card-header">
            <h4 class="card-title">
              <i class="pi pi-history me-2"></i>
              {{ "Order History" | translator }}
            </h4>
          </div>
          <div class="card-body">
            <div class="timeline">
              <div class="timeline-item" *ngFor="let historyItem of orderDetails.orderHistory; let i = index">
                <div class="timeline-marker">
                  <i class="pi" 
                     [class.pi-comment]="historyItem.type === 'COMMENT'" 
                     [class.pi-paperclip]="historyItem.type === 'ATTACHMENT'"
                     [class.pi-check-circle]="historyItem.type === 'STATUS_CHANGE' && historyItem.status === 'ACCEPTED'"
                     [class.pi-times-circle]="historyItem.type === 'STATUS_CHANGE' && historyItem.status === 'REJECTED'"
                     [class.pi-ban]="historyItem.type === 'STATUS_CHANGE' && historyItem.status === 'CANCELED'"
                     [class.pi-refresh]="historyItem.type === 'STATUS_CHANGE' && (historyItem.status === 'UPDATE' || historyItem.status === 'PENDING')"></i>
                </div>
                <div class="timeline-content">
                  <div class="timeline-header">
                    <span class="timeline-date">{{ formatDate(historyItem.createdAt) }}</span>
                  </div>
                  
                  <!-- Comment Display -->
                  <div class="timeline-body" *ngIf="historyItem.comments">
                    <p class="timeline-notes">{{ historyItem.comments }}</p>
                  </div>
                  
                  <!-- Attachment Display -->
                  <div class="timeline-body" *ngIf="historyItem.type === 'ATTACHMENT' && historyItem.attachment">
                    <div class="attachment-preview">
                      <!-- Image Preview -->
                      <div *ngIf="isImageFile(historyItem.attachment)" class="image-preview">
                        <img [src]="getFullImageUrl(historyItem.attachment)" 
                             [alt]="extractFileNameFromUrl(historyItem.attachment)"
                             class="preview-image"
                             (click)="openImageModal(historyItem.attachment)">
                        <div class="image-overlay">
                          <i class="pi pi-eye"></i>
                        </div>
                      </div>
                      
                      <!-- Document Preview -->
                      <div *ngIf="!isImageFile(historyItem.attachment)" class="document-preview">
                        <div class="document-icon">
                          <i class="pi" [class]="getFileIcon(getFileTypeFromUrl(historyItem.attachment))"></i>
                        </div>
                        <div class="document-info">
                          <h4 class="document-name">{{ extractFileNameFromUrl(historyItem.attachment) }}</h4>
                          <p class="document-type">{{ getFileTypeFromUrl(historyItem.attachment).toUpperCase() }} Document</p>
                        </div>
                      </div>
                      
                      <!-- Action Buttons -->
                      <div class="attachment-actions">
                        <button 
                          class="btn btn-outline-primary btn-sm"
                          (click)="openDocument(historyItem.attachment)"
                          title="Open Document">
                          <i class="pi pi-external-link"></i>
                          {{ "Open" | translator }}
                        </button>
                        <button 
                          class="btn btn-outline-secondary btn-sm"
                          (click)="downloadDocument(historyItem.attachment, extractFileNameFromUrl(historyItem.attachment))"
                          title="Download Document">
                          <i class="pi pi-download"></i>
                          {{ "Download" | translator }}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div class="timeline-meta">
                    {{'Created by' | translator}}: {{ getUserName(historyItem.createdBy) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Admin Actions for ACCEPTED/REJECTED Status -->
         <!-- {{permissionService.hasPermission('ORDER', 'u') | json}} -->
        <div *ngIf="isAcceptedOrRejected() && permissionService.hasPermission('ORDER', 'u')" class="detail-card mb-4">
          <div class="card-header">
            <h4 class="card-title">
              <i class="pi pi-cog me-2"></i>
              {{ "Admin Actions" | translator }}
            </h4>
          </div>
          <div class="card-body">
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <button 
                class="btn btn-warning" 
                (click)="updateOrderStatus('PENDING')"
                [disabled]="isUpdatingStatus"
                title="Request Update Again">
                <i class="pi pi-refresh"></i>
                {{ "Request Update" | translator }}
              </button>
              <button 
                class="btn btn-secondary" 
                (click)="updateOrderStatus('CANCELED')"
                [disabled]="isUpdatingStatus"
                title="Cancel Order">
                <i class="pi pi-ban"></i>
                {{ "Cancel Order" | translator }}
              </button>
            </div>
          </div>
        </div>

        <!-- Add Order History Card -->
        <div *ngIf="showAddInformationCard()" class="detail-card mb-4 77" >
          <div class="card-header">
            <h4 class="card-title">
              <!-- <i class="pi pi-plus-circle me-2"></i> -->
              <i class="fa-solid fa-chart-simple me-2"></i>
              {{ "Add Information" | translator }}
            </h4>
          </div>
          <div class="card-body">
            <form [formGroup]="historyForm" (ngSubmit)="submitHistoryItem()">
              <!-- History Type Selection -->
              <div class="form-group">
                <!-- <label class="form-label">{{ "Information Type" | translator }}</label> -->
                <div class="history-type-buttons">
                  <button 
                    type="button"
                    class="btn btn-outline-primary"
                    [class.active]="selectedHistoryType === 'COMMENT'"
                    (click)="onHistoryTypeChange('COMMENT')">
                    <i class="pi pi-comment"></i>
                    {{ "Comment" | translator }}
                  </button>
                  <button 
                    type="button"
                    class="btn btn-outline-success"
                    [class.active]="selectedHistoryType === 'ATTACHMENT'"
                    (click)="onHistoryTypeChange('ATTACHMENT')">
                    <i class="pi pi-paperclip"></i>
                    {{ "Attachment" | translator }}
                  </button>
                </div>
              </div>

              <!-- Comment Input -->
              <div class="form-group" *ngIf="selectedHistoryType === 'COMMENT'">
                  <label for="comment" class="form-label">{{ "Comment" | translator }} <span class="required">*</span></label>
                <textarea 
                  id="comment" 
                  formControlName="comment" 
                  class="form-control"
                  rows="4"
                  placeholder="{{ 'Enter your comment here...' | translator }}"
                  [class.is-invalid]="historyForm.get('comment')?.invalid && historyForm.get('comment')?.touched">
                </textarea>
                <div *ngIf="historyForm.get('comment')?.invalid && historyForm.get('comment')?.touched" class="invalid-feedback">
                  {{ "Comment is required" | translator }}
                </div>
              </div>

              <!-- File Upload -->
              <div class="form-group" *ngIf="selectedHistoryType === 'ATTACHMENT'">
                <label class="form-label">{{ "Upload File" | translator }} <span class="required">*</span></label>
                <div class="d-flex align-items-end gap-2">
                  <div class="flex-grow-1">
                    <app-file-upload
                      label="{{ 'Choose File' | translator }}"
                      innerLabe="{{ 'Click to upload file' | translator }}"
                      accept="*/*"
                      [imageType]="imageTypes.training_material"
                      (response)="onFileUploaded($event)">
                    </app-file-upload>
                  </div>
                  <button 
                    type="submit" 
                    class="v4-btn-every"
                    [disabled]="isSubmittingHistory || !uploadedFileUrl">
                    <i class="pi pi-upload" *ngIf="!isSubmittingHistory"></i>
                    <i class="pi pi-spinner pi-spin" *ngIf="isSubmittingHistory"></i>
                    {{ isSubmittingHistory ? ('Uploading...' | translator) : ('Upload' | translator) }}
                  </button>
                </div>
                
                <!-- Uploaded Document Display -->
                <div *ngIf="uploadedFileUrl" class="uploaded-document-section">
                  <div class="document-preview">
                    <!-- Image Preview -->
                    <div *ngIf="isImageFile(uploadedFileUrl)" class="image-preview">
                      <img [src]="getFullImageUrl(uploadedFileUrl)" 
                           [alt]="uploadedFileName"
                           class="preview-image m-l-2">
                    </div>
                    
                    <!-- Document Preview -->
                    <div *ngIf="!isImageFile(uploadedFileUrl)" class="document-preview">
                      <div class="document-icon">
                        <i class="pi" [class]="getFileIcon(uploadedFileType)"></i>
                      </div>
                      <div class="document-info">
                        <h4 class="document-name">{{ uploadedFileName }}</h4>
                        <p class="document-type">{{ uploadedFileType.toUpperCase() }} Document</p>
                      </div>
                    </div>
                    
                    <div class="document-actions">
                      <button 
                        type="button" 
                        class="btn btn-outline-primary btn-sm"
                        (click)="openDocument(uploadedFileUrl)"
                        title="Open Document">
                        <i class="pi pi-external-link"></i>
                        {{ "Open" | translator }}
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-outline-secondary btn-sm"
                        (click)="downloadDocument(uploadedFileUrl, uploadedFileName)"
                        title="Download Document">
                        <i class="pi pi-download"></i>
                        {{ "Download" | translator }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Submit Button -->
              <div class="form-actions">
                <div class="d-flex justify-content-between align-items-center gap-2 flex-wrap w-100">
                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <button 
                      type="button" 
                      class="v4-btn-every"
                      (click)="resetHistoryForm()"
                      [disabled]="isSubmittingHistory">
                      <i class="pi pi-refresh"></i>
                      {{ "Reset" | translator }}
                    </button>
                    
                    <button 
                      class="btn btn-success" 
                      *ngIf="permissionService.hasPermission('ORDER', 'u') && orderDetails.orderStatus === 'PENDING'"
                      (click)="updateOrderStatus('ACCEPTED')"
                      [disabled]="isUpdatingStatus"
                      title="Accept Order">
                      <i class="pi pi-check"></i>
                      {{ "Accept Order" | translator }}
                    </button>
                    <button 
                      class="btn btn-warning" 
                      (click)="updateOrderStatus('UPDATE')"
                      *ngIf="permissionService.hasPermission('ORDER', 'u') && orderDetails.orderStatus === 'PENDING'"
                      [disabled]="isUpdatingStatus"
                      title="Request Update">
                      <i class="pi pi-refresh"></i>
                      {{ "Request Update" | translator }}
                    </button>
                    <button 
                      class="btn btn-info" 
                      (click)="updateOrderStatus('UPDATE_SURVEY')"
                      *ngIf="permissionService.hasPermission('ORDER', 'u') && orderDetails.orderStatus === 'PENDING' && orderDetails.survey"
                      [disabled]="isUpdatingStatus"
                      title="Request Survey Update">
                      <i class="pi pi-file-edit"></i>
                      {{ "Request Survey Update" | translator }}
                    </button>
                    <button 
                      class="btn btn-danger" 
                      (click)="updateOrderStatus('REJECTED')"
                      *ngIf="permissionService.hasPermission('ORDER', 'u') && orderDetails.orderStatus === 'PENDING'"
                      [disabled]="isUpdatingStatus"
                      title="Reject Order Permanently">
                      <i class="pi pi-times"></i>
                      {{ "Reject Order" | translator }}
                    </button>
                    <button 
                      class="btn btn-warning" 
                      (click)="updateOrderStatus('PENDING')"
                      *ngIf="(orderDetails.orderStatus === 'UPDATE' || orderDetails.orderStatus === 'PENDING') && !permissionService.hasPermission('ORDER', 'u')"
                      [disabled]="isUpdatingStatus"
                      title="Submit Order">
                      <i class="pi pi-send"></i>
                      {{ "Submit Order" | translator }}
                    </button>
                  </div>
                  <button 
                    *ngIf="selectedHistoryType === 'COMMENT'"
                    type="submit" 
                    class="v4-btn-every"
                    [disabled]="isSubmittingHistory || historyForm.invalid">
                    <i class="pi pi-save" *ngIf="!isSubmittingHistory"></i>
                    <i class="pi pi-spinner pi-spin" *ngIf="isSubmittingHistory"></i>
                    {{ isSubmittingHistory ? ('Saving...' | translator) : ('Save' | translator) }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="col-lg-4">
         <!-- Customer Information -->
         <div class="detail-card mb-4 44">
          <div class="card-header">
            <h4 class="card-title">
              <i class="pi pi-user me-2"></i>
              {{ "Customer Information" | translator }}
            </h4>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6">
                <div class="info-item">
                  <label class="info-label">{{ "the name" | translator }}</label>
                  <p class="info-value">{{ orderDetails.customer.name }}</p>
                </div>
                <div class="info-item">
                  <label class="info-label">{{ "Email" | translator }}</label>
                  <p class="info-value email-with-copy">
                    <a [href]="'mailto:' + orderDetails.customer.email">
                      {{ truncateEmail(orderDetails.customer.email, 20) }}
                    </a>
                    <button class="copy-email-btn" 
                            (click)="copyEmailToClipboard(orderDetails.customer.email)"
                            title="{{ 'Copy Email' | translator }}">
                      <i class="pi pi-copy"></i>
                    </button>
                  </p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-item" *ngIf="orderDetails.customer.phone">
                  <label class="info-label">{{ "Phone" | translator }}</label>
                  <p class="info-value">
                    <a [href]="'tel:' + orderDetails.customer.phone">
                      {{ orderDetails.customer.phone }}
                    </a>
                  </p>
                </div>
                <div class="info-item" *ngIf="orderDetails.customer.organization">
                  <label class="info-label">{{ "Entity Name" | translator }}</label>
                  <p class="info-value">{{ orderDetails.customer.organization }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Order Summary -->
        <div class="detail-card mb-4 55">
          <div class="card-header">
            <h4 class="card-title">
              <i class="pi pi-info-circle me-2"></i>
              {{ "Order Summary" | translator }}
            </h4>
          </div>
          <div class="card-body">
            <div class="summary-item">
              <span class="summary-label">{{ "Order ID" | translator }}:</span>
              <span class="summary-value">{{ orderDetails.orderId }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">{{ "Status" | translator }}:</span>
              <p-tag 
                [value]="translate(orderDetails.orderStatus)" 
                [severity]="getStatusSeverity(orderDetails.orderStatus)">
              </p-tag>
            </div>
            <div class="summary-item">
              <span class="summary-label">{{ "Created" | translator }}:</span>
              <span class="summary-value">{{ formatDate(orderDetails.createdAt) }}</span>
            </div>
            <!-- <div class="summary-item" *ngIf="orderDetails.assignedTo">
              <span class="summary-label">Assigned To:</span>
              <span class="summary-value">{{ orderDetails.assignedTo }}</span>
            </div> -->
            <div class="summary-item">
              <span class="summary-label">{{ "Total Amount" | translator }}:</span>
              <span class="summary-value total-amount">
                {{ formatCurrency(orderDetails.productService.totalAmount, orderDetails.productService.currency) }}
                <img *ngIf="isValidNumber(orderDetails.productService.totalAmount)" src="assets/pdf/graphics/Saudi_Riyal_Symbol-1.png" alt="SAR" class="currency-icon-inline currency-14" />
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</div>
</div>

<app-footer></app-footer>

<!-- Image Modal -->
<p-dialog 
  [(visible)]="showImageModal" 
  [modal]="true" 
  [closable]="true" 
  [draggable]="false" 
  [resizable]="false"
  [style]="{width: '90vw', maxWidth: '800px'}"
  [header]="selectedImageName"
  (onHide)="closeImageModal()">
  
  <div class="image-modal-content" *ngIf="selectedImageUrl">
    <img [src]="getFullImageUrl(selectedImageUrl)" 
         [alt]="selectedImageName"
         class="modal-image">
  </div>
</p-dialog>

<!-- Survey Modal -->
<p-dialog 
  [(visible)]="showSurveyModal" 
  [modal]="true" 
  [closable]="true" 
  [draggable]="false" 
  [resizable]="false"
  [style]="{width: '90vw', maxWidth: '1000px'}"
  [header]="surveyData?.name || 'Survey Details'"
  (onHide)="closeSurveyModal()">
  
  <div class="survey-modal-content" *ngIf="surveyData">
    <!-- Survey Header Information -->
    <!-- <div class="survey-header mb-4">
      <div class="row">
        <div class="col-md-6">
          <div class="info-item">
            <label class="info-label">{{ "Survey Name" | translator }}</label>
            <p class="info-value">{{ surveyData.name }}</p>
          </div>
          <div class="info-item" *ngIf="surveyData.type">
            <label class="info-label">{{ "Survey Type" | translator }}</label>
            <p class="info-value">{{ surveyData.type.name }}</p>
          </div>
        </div>
        <div class="col-md-6">
          <div class="info-item" *ngIf="surveyData.status">
            <label class="info-label">{{ "Status" | translator }}</label>
            <p class="info-value">{{ surveyData.status }}</p>
          </div>
          <div class="info-item" *ngIf="surveyData.questions">
            <label class="info-label">{{ "Total Questions" | translator }}</label>
            <p class="info-value">{{ surveyData.questions.length }}</p>
          </div>
        </div>
      </div>
    </div> -->

    <!-- Survey Questions -->
    <div class="survey-questions" *ngIf="surveyData.questions && surveyData.questions.length > 0">
      <h5 class="mb-3">
        <i class="pi pi-question-circle me-2"></i>
        {{ "Survey Questions & Answers" | translator }}
      </h5>
      <app-popupview-questions 
        [(currentQuestion)]="surveyData.questions"
        [readonly]="isSurveyReadonly()">
      </app-popupview-questions>
    </div>

   
  </div>
  
  <ng-template pTemplate="footer">
    <button 
      type="button" 
      class="btn btn-secondary" 
      (click)="closeSurveyModal()">
      {{ "Close" | translator }}
    </button>
    <button 
      *ngIf="canEditSurvey()"
      type="button" 
      class="btn btn-primary" 
      (click)="saveSurveyAndUpdateOrder()"
      [disabled]="isSavingSurvey || isUpdatingStatus">
      <i class="pi pi-save" *ngIf="!isSavingSurvey && !isUpdatingStatus"></i>
      <i class="pi pi-spinner pi-spin" *ngIf="isSavingSurvey || isUpdatingStatus"></i>
      {{ (isSavingSurvey || isUpdatingStatus) ? ('Saving...' | translator) : ('Save & Update Order' | translator) }}
    </button>
  </ng-template>
</p-dialog>